import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SectionHeader, Ornament, Reveal } from "./Ornament";

export default function DetailPage({ kicker, name, intro, longIntro, image, video, gallery = [], tags = [], meta = [] }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [name]);

  return (
    <div className="pt-28" data-testid="detail-page">
      {/* Back nav */}
      <div className="container-lux flex items-center justify-between pt-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 font-micro text-[10px] text-[var(--lu-gold-dark)] hover:text-[var(--lu-gold)]"
          data-testid="detail-back"
        >
          <ArrowLeft size={14} />Back</button>
        <button
          onClick={() => navigate("/contact")}
          className="btn-gold-outline text-[10px] px-6 py-2"
          data-testid="detail-book"
        >
          Book
        </button>
      </div>

      {/* Hero image */}
      <section className="relative bg-[var(--lu-black)] pt-14">
        <div className="container-lux">
          <p className="kicker text-center">{kicker}</p>
          <h1 className="mt-6 h-editorial text-[52px] sm:text-[72px] lg:text-[104px] text-center text-[var(--lu-ivory)]">
            {name}
          </h1>
          <Ornament className="mt-8" />

          <div className="mt-12 relative tile aspect-[16/8]">
            {video ? (
              <video
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={image}
                data-testid="detail-hero-video"
              >
                <source src={video} type="video/mp4" />
              </video>
            ) : (
              <img src={image} alt={name} className="w-full h-full object-cover" />
            )}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-y">
        <div className="container-lux grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <p className="font-serif italic text-[26px] leading-[1.4] text-[var(--lu-black)]">
              {intro}
            </p>
            {longIntro && (
              <p className="mt-8 text-[16px] leading-[1.9] text-black-70 font-light">
                {longIntro}
              </p>
            )}
            {tags.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-3">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="font-micro text-[10px] px-4 py-2 border border-[var(--lu-gold-dark)]/60 text-[var(--lu-gold-dark)]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {meta.length > 0 && (
            <aside className="lg:col-span-5">
              <div className="border border-[var(--lu-gold-dark)]/40 p-8 bg-[var(--lu-cream)]/50">
                <p className="kicker text-[var(--lu-gold-dark)]">At a glance</p>
                <ul className="mt-6 space-y-4">
                  {meta.map((m) => (
                    <li key={m.label}>
                      <p className="font-micro text-[9px] text-black-60">{m.label}</p>
                      <p className="font-serif text-[19px] text-[var(--lu-black)] mt-1">{m.value}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="section-y bg-[var(--lu-black)]">
          <div className="container-lux">
            <SectionHeader kicker="Gallery" title="Moments" italicTitle="from the evening" light />
            <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
              {gallery.map((src, i) => (
                <Reveal key={i} delay={Math.min((i % 4) + 1, 6)}>
                  <div className="tile aspect-[4/5]">
                    <img src={src} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-y">
        <div className="container-lux text-center">
          <SectionHeader kicker="Let's create" title="Bring this" italicTitle="in your evening." />
          <div className="mt-12 flex flex-col sm:flex-row gap-5 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="btn-gold"
              data-testid="detail-cta-proposal"
            >
              Request Proposal <ArrowUpRight size={16} />
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="btn-gold-outline"
              data-testid="detail-cta-discovery"
            >Close Discovery Call</button>
          </div>
        </div>
      </section>
    </div>
  );
}
